import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faLock,
  faEnvelope,
  faPhone,
  faArrowLeft,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../css/Register.css';
import Navbar from '../landingComponents/Navbar';
import Footer from '../landingComponents/Footer';
import logo from '../images/logo.png';
import Landing from '../images/login.png';
import axios from 'axios';
import { BaseUrl } from '../Services/Api';

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    fatherName: '',
    username: '',
    password: '',
    confirmPassword: '',
    state: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(null);
  const [apiError, setApiError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Validation for alphabets only
    if ((name === 'firstName' || name === 'lastName') && !/^[A-Za-z]*$/.test(value)) {
      return;
    }

    // Phone number numeric only + max 10 digits
    if (name === 'phone') {
      if (!/^[0-9]*$/.test(value)) return;
      if (value.length > 10) return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name required';
    if (!formData.email.trim()) newErrors.email = 'Email required';
    else if (!/^\S+@\S+\.\S+$/.test(formData.email)) newErrors.email = 'Invalid email';
    if (!formData.phone.trim()) newErrors.phone = 'Phone required';
    else if (formData.phone.length !== 10) newErrors.phone = 'Phone must be 10 digits';
    if (!formData.username.trim()) newErrors.username = 'Username required';
    if (!formData.password) newErrors.password = 'Password required';
    else if (formData.password.length < 6) newErrors.password = 'Minimum 6 characters';
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords must match';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validate()) {
      setIsSubmitting(true);
      setApiError(null);

      try {
        const response = await fetch(`${BaseUrl}/intt-register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });

        const responseData = await response.json();

        if (response.ok) {
          toast.success("Registration successful! Redirecting to login...", {
            autoClose: 2000,
            onClose: () => navigate("/login"),
          });

          setFormData({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            company: '',
            fatherName: '',
            username: '',
            password: '',
            confirmPassword: '',
            state: ''
          });
        } else {
          if (responseData.errors) {
            const apiErrors = {};
            Object.keys(responseData.errors).forEach(key => {
              apiErrors[key] = responseData.errors[key][0];
            });
            setErrors(apiErrors);
            setApiError('Please fix the errors below');
          } else {
            setApiError(responseData.message || 'Registration failed. Please try again.');
          }
        }
      } catch (error) {
        setApiError('Network error. Please check your connection and try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  useEffect(() => {
    document.title = 'Register';
  }, []);

  return (
    <>
      <ToastContainer />
      <div className="registration-container">
        <div className="registration-wrapper">
          <div className="registration-grid">
            <div className="registration-image-container">
              <img src={Landing} alt="Registration" className="registration-image" />
              <div className="image-overlay">
                <h2>Join Our LSC Community</h2>
                <p>Create an account to access exclusive features</p>
              </div>
            </div>

            <div className="registration-form-container">
              <div className="form-header">
                <button onClick={() => navigate('/')} className="btn-back">
                  <FontAwesomeIcon icon={faArrowLeft} className="me-2" /> Home
                </button>
                <div className='logo-container'>
                  <img src={logo} alt="Company Logo" className="branding-logo1" />
                </div>
                <h1 className="branding-title">Online Certificate Programmes</h1>
              </div>

              <form onSubmit={handleSubmit} className="registration-form">
                {apiError && <div className="alert alert-danger">{apiError}</div>}

                <div className="form-content">
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">First Name</label>
                      <div className="input-with-icon">
                        <FontAwesomeIcon icon={faUser} className="input-icon" />
                        <input
                          type="text"
                          className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          placeholder="First name"
                        />
                      </div>
                      {errors.firstName && <div className="error-message">{errors.firstName}</div>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">Last Name</label>
                      <div className="input-with-icon">
                        <FontAwesomeIcon icon={faUser} className="input-icon" />
                        <input
                          type="text"
                          className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          placeholder="Last name"
                        />
                      </div>
                      {errors.lastName && <div className="error-message">{errors.lastName}</div>}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <div className="input-with-icon">
                      <FontAwesomeIcon icon={faEnvelope} className="input-icon" />
                      <input
                        type="email"
                        className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Email"
                      />
                    </div>
                    {errors.email && <div className="error-message">{errors.email}</div>}
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Phone</label>
                      <div className="input-with-icon">
                        <FontAwesomeIcon icon={faPhone} className="input-icon" />
                        <input
                          type="text"
                          className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="Phone"
                        />
                      </div>
                      {errors.phone && <div className="error-message">{errors.phone}</div>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">Username</label>
                      <div className="input-with-icon">
                        <FontAwesomeIcon icon={faUser} className="input-icon" />
                        <input
                          type="text"
                          className={`form-control ${errors.username ? 'is-invalid' : ''}`}
                          name="username"
                          value={formData.username}
                          onChange={handleChange}
                          placeholder="Username"
                        />
                      </div>
                      {errors.username && <div className="error-message">{errors.username}</div>}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Password</label>
                      <div className="input-with-icon">
                        <FontAwesomeIcon icon={faLock} className="input-icon" />
                        <input
                          type="password"
                          className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          placeholder="Password"
                        />
                      </div>
                      {errors.password && <div className="error-message">{errors.password}</div>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">Confirm Password</label>
                      <div className="input-with-icon">
                        <FontAwesomeIcon icon={faLock} className="input-icon" />
                        <input
                          type="password"
                          className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          placeholder="Confirm password"
                        />
                      </div>
                      {errors.confirmPassword && <div className="error-message">{errors.confirmPassword}</div>}
                    </div>
                  </div>
                </div>

                <button type="submit" className="btn-register" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      <span className="ms-2">Creating Account...</span>
                    </>
                  ) : 'Create Account'}
                </button>

                <div className="form-footer">
                  <p>
                    Already have an account?{' '}
                    <button type="button" className="btn-link" onClick={() => navigate('/login')}>
                      Sign In
                    </button>
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Register;
